import { studentNumberLabel } from "@classroom/domain";
import { i18n } from "../i18n";
import type { RecordReport } from "./recordExports";

type PrintCopy = { board: string; teacher: string; student: string; emptySeat: string; noSeating: string };
const escapeHtml = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character] || character,
  );

function seatingRoomSize(chart: NonNullable<RecordReport["seating"]>["chart"]) {
  if (!chart) return { width: 1, height: 1 };
  // Use the room editor's seat and aisle proportions, including space for the board.
  return {
    width: chart.columns * 84 + (chart.columns - 1) * 12 + chart.columnAisles.length * 38,
    height: chart.rows * 82 + (chart.rows - 1) * 10 + chart.rowAisles.length * 30 + 40,
  };
}

export function recordPrintLandscape(report: RecordReport): boolean {
  if (report.seating?.chart) {
    const room = seatingRoomSize(report.seating.chart);
    return Math.min(273 / room.width, 161 / room.height) >= Math.min(186 / room.width, 248 / room.height);
  }
  return Boolean(report.landscape || report.seating) || report.sheets.some((sheet) => sheet.headers.length > 5);
}

export function buildRecordPrintHtml(report: RecordReport, copy: PrintCopy): string {
  const chart = report.seating?.chart;
  const landscape = recordPrintLandscape(report);
  let content: string;
  if (report.seating) {
    const teacher = chart?.view === "teacher";
    const board = `<div class="board" style="grid-row:${teacher ? 3 : 1}">${escapeHtml(copy.board)}</div>`;
    const students = new Map(report.seating.students.map((student) => [student.id, student]));
    const room = seatingRoomSize(chart);
    const tracks = (count: number, aisles: number[], seat: number, normal: number, wide: number) =>
      Array.from(
        { length: count },
        (_, index) =>
          `minmax(0, ${seat}fr)${index < count - 1 ? ` ${aisles.includes(teacher ? count - 2 - index : index) ? wide : normal}fr` : ""}`,
      ).join(" ");
    const grid = chart
      ? `<div class="seating-room"><div class="room-plan" style="width:min(100cqw, calc(100cqh * ${room.width / room.height}));aspect-ratio:${room.width}/${room.height};grid-template-rows:${teacher ? `${room.height - 40}fr 12fr 28fr` : `28fr 12fr ${room.height - 40}fr`};--seat-font:clamp(9pt, calc(100cqw * ${14 / room.width}), 32pt)">${!teacher ? board : ""}<div class="seats" role="table" aria-label="${escapeHtml(report.title)}" style="grid-row:${teacher ? 1 : 3};grid-template-rows:${tracks(chart.rows, chart.rowAisles, 82, 10, 40)};grid-template-columns:${tracks(chart.columns, chart.columnAisles, 84, 12, 50)}">${Array.from(
          { length: chart.rows },
          (_, vr) => {
            const row = teacher ? chart.rows - 1 - vr : vr;
            return `<div class="seat-row" role="row">${Array.from({ length: chart.columns }, (_, vc) => {
              const col = teacher ? chart.columns - 1 - vc : vc;
              const student = students.get(chart.seats[`${row}_${col}`]);
              return `<div class="seat" role="cell" style="grid-row:${vr * 2 + 1};grid-column:${vc * 2 + 1}">${student ? `<b>${escapeHtml(studentNumberLabel(student))}</b><span>${escapeHtml(student.name)}</span>` : escapeHtml(copy.emptySeat)}</div>`;
            }).join("")}</div>`;
          },
        ).join("")}</div>${teacher ? board : ""}</div></div>`
      : `<p>${escapeHtml(copy.noSeating)}</p>`;
    content = `<p>${escapeHtml(teacher ? copy.teacher : copy.student)}</p>${grid}`;
  } else {
    content = report.sheets
      .map(
        (sheet) =>
          `<section><h2>${escapeHtml(sheet.title)}</h2><table><thead><tr>${sheet.headers.map((header) => `<th scope="col">${escapeHtml(header)}</th>`).join("")}</tr></thead><tbody>${sheet.rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`).join("")}</tbody></table></section>`,
      )
      .join("");
  }
  return `<!doctype html><html lang="${i18n.resolvedLanguage === "en" ? "en" : "zh-HK"}"><head><meta charset="utf-8"><title>${escapeHtml(report.className)}｜${escapeHtml(report.title)}</title><style>
  @page { size: A4 ${landscape ? "landscape" : "portrait"}; margin: 12mm; }
  @media screen { body { width: ${landscape ? 297 : 210}mm; min-height: ${landscape ? 210 : 297}mm; padding: 12mm; background: white; } }
  @media print { body { width: ${landscape ? 273 : 186}mm; } }
  * { box-sizing: border-box; } body { margin: 0; color: #202a26; font: 11pt "Noto Sans HK", "PingFang TC", "Microsoft JhengHei", sans-serif; }
  h1 { font-size: 18pt; margin: 0 0 5mm; overflow-wrap: anywhere; } .class-name { margin-bottom: 7mm; font-weight: bold; }
  h2 { font-size: 13pt; margin: 6mm 0 3mm; break-after: avoid; } table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  thead { display: table-header-group; } th, td { padding: 2.5mm; border: 1px solid #a9b5ae; text-align: left; overflow-wrap: anywhere; white-space: pre-wrap; vertical-align: top; }
  th { font-weight: bold; background: #f1f4f2; } tr { break-inside: avoid; } section + section { margin-top: 8mm; }
  .seating-paper { height: ${landscape ? 185 : 272}mm; display: flex; flex-direction: column; gap: 2.5mm; }
  .seating-print h1 { flex-shrink: 0; font-size: 16pt; margin: 0; } .seating-print .class-name, .seating-print p { flex-shrink: 0; margin: 0; }
  .seating-room { flex: 1; min-height: 0; display: grid; place-items: center; container-type: size; }
  .room-plan { display: grid; container-type: inline-size; }
  .seats { display: grid; container-type: inline-size; } .seat-row { display: contents; }
  .seat { min-width: 0; min-height: 0; padding: 1mm; border: 1px solid #a9b5ae; border-radius: 2mm; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; overflow-wrap: anywhere; line-height: 1.15; font-size: var(--seat-font); }
  .seat b { font-size: .8em; } .seat span { width: 100%; }
  .board { justify-self: center; width: 55%; height: 100%; margin: 0; border: 2px solid #596b61; display: grid; place-items: center; text-align: center; font-weight: bold; font-size: var(--seat-font); }
  </style></head><body${report.seating ? ' class="seating-print"' : ""}>${report.seating ? '<div class="seating-paper">' : ""}<h1>${escapeHtml(report.title)}</h1><div class="class-name">${escapeHtml(report.className)}</div>${content}${report.seating ? "</div>" : ""}</body></html>`;
}

export async function printRecordReport(report: RecordReport, copy: PrintCopy): Promise<void> {
  await printHtml(buildRecordPrintHtml(report, copy));
}

export type PdfPrintPage = { image: string; width: number; height: number };

export async function printRecordPdf(pages: PdfPrintPage[], title: string): Promise<void> {
  const size = pages[0];
  if (!size) throw new Error("pdf-pages");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
    @page { size: ${size.width}pt ${size.height}pt; margin: 0; }
    body { margin: 0; }
    .pdf-print-page { width: ${size.width}pt; height: ${size.height}pt; break-after: page; break-inside: avoid; }
    .pdf-print-page:last-child { break-after: auto; }
    img { display: block; width: 100%; height: 100%; object-fit: contain; }
  </style></head><body>${pages.map((page, index) => `<div class="pdf-print-page"><img src="${escapeHtml(page.image)}" alt="${escapeHtml(i18n.t("localExport:pdfPage", { page: index + 1, count: pages.length }))}"></div>`).join("")}</body></html>`;
  await printHtml(html);
}

async function printHtml(html: string): Promise<void> {
  const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;width:1px;height:1px;left:-10000px;border:0";
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  frame.dataset.recordPrint = "true";
  const loaded = new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      frame.remove();
      reject(new Error("print-load"));
    }, 10_000);
    frame.onload = () => {
      window.clearTimeout(timeout);
      resolve();
    };
  });
  frame.srcdoc = html;
  document.body.append(frame);
  try {
    await loaded;
    const target = frame.contentWindow;
    if (!target) throw new Error("print-frame");
    await target.document.fonts.ready;
    await Promise.all(Array.from(target.document.images, (image) => image.decode()));
    target.addEventListener(
      "afterprint",
      () => {
        frame.remove();
        previousFocus?.focus();
      },
      { once: true },
    );
    target.focus();
    target.print();
    previousFocus?.focus();
    // Some browsers do not send afterprint for a cancelled print dialog.
    window.setTimeout(() => frame.remove(), 300_000);
  } catch (error) {
    frame.remove();
    previousFocus?.focus();
    throw error;
  }
}
